import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

const propTypes = {
  isOpen         : PropTypes.bool,
  toggle         : PropTypes.func.isRequired,
  defaultSkuList : PropTypes.string,
  defaultAction  : PropTypes.oneOf([ 'sold', 'on_hold' ]),
  pageUtils      : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  }),
  onCompleted    : PropTypes.func
}

const normalizeSkuLines = (value = '') => value
  .split(/\r?\n/)
  .map((sku) => sku.trim())
  .filter(Boolean)

const BulkMarkSoldOnHoldModal = ({
  isOpen,
  toggle,
  defaultSkuList = '',
  defaultAction = 'sold',
  pageUtils,
  onCompleted
}) => {
  const [ skuList, setSkuList ]       = useState(defaultSkuList)
  const [ action, setAction ]         = useState(defaultAction)
  const [ isSubmitting, setSubmitting ] = useState(false)
  const [ result, setResult ]         = useState(null)

  useEffect(() => {
    if (!isOpen) return

    setSkuList(defaultSkuList || '')
    setAction(defaultAction || 'sold')
    setResult(null)
  }, [ defaultAction, defaultSkuList, isOpen ])

  const skuCount = useMemo(() => new Set(normalizeSkuLines(skuList)).size, [ skuList ])
  const warningResults = useMemo(() => (
    (result?.results || []).filter((item) => item.status === 'warning')
  ), [ result ])
  const failedResults = useMemo(() => (
    (result?.results || []).filter((item) => ![ 'updated', 'warning' ].includes(item.status))
  ), [ result ])

  const closeModal = () => {
    if (isSubmitting) return
    toggle()
  }

  const submit = async () => {
    if (isSubmitting) return
    if (!skuCount) {
      pageUtils?.setApiErrorMsg?.({ messages: { base: 'Please input at least one SKU.' } })
      return
    }

    setSubmitting(true)
    setResult(null)

    try {
      const response = await InventoriesApiV2.bulkMarkSoldOnHold({
        sku_list : skuList,
        action
      })

      setResult(response.data)
      pageUtils?.setAlertMsg?.(response.data?.message || 'Bulk inventory status update completed.')
      onCompleted?.(response.data)
    } catch (error) {
      pageUtils?.setApiErrorMsg?.(error.response?.data || { messages: { base: error.message } })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={ isOpen } toggle={ closeModal } centered size='lg'>
      <ModalHeader toggle={ isSubmitting ? undefined : closeModal }>
        Bulk Mark Products as Sold or On Hold
      </ModalHeader>
      <ModalBody>
        <Label className='font-weight-bold'>SKU List</Label>
        <Input
          type='textarea'
          rows='10'
          value={ skuList }
          disabled={ isSubmitting }
          onChange={ (event) => setSkuList(event.target.value) }
          placeholder={ 'Masukkan SKU dipisahkan dengan Enter:\nBKR-HR01-311225-0000\nBKR-HR01-311225-0000' }
        />
        <div className='small text-muted mt-1'>{ skuCount } unique SKU(s). One SKU per line; duplicate lines are ignored.</div>

        <div className='font-weight-bold mt-4 mb-2'>Action:</div>
        <FormGroup check className='mb-2'>
          <Label check>
            <Input type='radio' name='bulk-mark-status-action' value='sold' checked={ action === 'sold' } disabled={ isSubmitting } onChange={ () => setAction('sold') } />
            {' '}Mark as Sold
          </Label>
          <div className='small text-muted ml-4'>BKC Photo-Only: boleh tanpa Deal jika barang terjual di seller; SQ dan IQ menjadi 0. Selain BKC: wajib termasuk dalam Deal aktif.</div>
        </FormGroup>
        <FormGroup check>
          <Label check>
            <Input type='radio' name='bulk-mark-status-action' value='on_hold' checked={ action === 'on_hold' } disabled={ isSubmitting } onChange={ () => setAction('on_hold') } />
            {' '}Mark as On Hold
          </Label>
          <div className='small text-muted ml-4'>Each SKU requires SQ &gt; 0. SQ becomes 0; IQ stays unchanged.</div>
        </FormGroup>

        { result && (
          <div className='border rounded p-3 mt-4'>
            <div className='font-weight-bold'>{ result.message }</div>
            <div className='small text-muted mt-1'>
              Requested: { result.summary?.requested_count ?? 0 } · Updated: { result.summary?.updated_count ?? 0 } · Warnings: { result.summary?.warning_count ?? 0 } · Failed: { result.summary?.failed_count ?? 0 }
            </div>
            { warningResults.length > 0 && (
              <div className='alert alert-warning mt-3 mb-0'>
                <div className='font-weight-bold mb-1'>Warnings / skipped</div>
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  { warningResults.map((item, index) => (
                    <div key={ `${item.sku || 'sku'}-${index}` } className='small border-top py-2'>
                      <strong>{ item.sku || '-' }</strong> — { item.message || item.status }
                    </div>
                  )) }
                </div>
              </div>
            ) }
            { failedResults.length > 0 && (
              <div className='mt-3'>
                <div className='font-weight-bold mb-1'>Not updated</div>
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  { failedResults.map((item, index) => (
                    <div key={ `${item.sku || 'sku'}-${index}` } className='small border-top py-2'>
                      <strong>{ item.sku || '-' }</strong> — { item.message || item.status }
                    </div>
                  )) }
                </div>
              </div>
            ) }
          </div>
        ) }
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' disabled={ isSubmitting } onClick={ closeModal }>
          { result ? 'Close' : 'Cancel' }
        </Button>
        <Button color='primary' disabled={ isSubmitting || !skuCount } onClick={ submit }>
          { isSubmitting ? 'Processing...' : 'Submit' }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

BulkMarkSoldOnHoldModal.propTypes = propTypes

export default BulkMarkSoldOnHoldModal
